import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Link,
  Chip,
  Alert,
  alpha,
} from "@mui/material";
import { HybridIOC_V2 } from "../types";
import { cyber } from "../theme";

/**
 * Defines the props for the HybridIOCTable component.
 * It now accepts an array of IOCs directly.
 */
interface HybridIOCTableProps {
  iocs: HybridIOC_V2[];
}

/**
 * Renders a table of advanced IOCs from a provided array.
 * This component no longer fetches its own data.
 */
const HybridIOCTable: React.FC<HybridIOCTableProps> = ({ iocs }) => {
  if (!iocs || iocs.length === 0) {
    return <Alert severity="info">No advanced IOCs found in this report.</Alert>;
  }

  /**
   * Determines the color of the confidence chip based on the confidence level.
   * @param confidence The confidence level of the IOC.
   * @returns A MUI color string.
   */
  const getConfidenceColor = (confidence: 'low' | 'medium' | 'high') => {
    switch (confidence) {
      case 'low':
        return 'warning';
      case 'medium':
        return 'primary';
      case 'high':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <>
      <Typography variant="h6" component="h2" gutterBottom>
        Advanced Indicators of Compromise
      </Typography>
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Value</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Confidence</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {iocs.map((ioc, index) => (
              <TableRow key={index} hover>
                <TableCell
                  sx={{
                    wordBreak: 'break-all',
                    fontFamily: cyber.fontMono,
                    fontSize: '0.8rem',
                    color: alpha(cyber.accent.cyan, 0.9),
                  }}
                >
                  {ioc.virustotal_link ? (
                    <Link
                      href={ioc.virustotal_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontFamily: 'inherit',
                        color: alpha(cyber.accent.cyan, 0.9),
                        textDecorationColor: alpha(cyber.accent.cyan, 0.4),
                      }}
                    >
                      {ioc.value}
                    </Link>
                  ) : (
                    ioc.value
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={ioc.type} size="small" />
                </TableCell>
                <TableCell>
                  <Chip label={ioc.confidence} color={getConfidenceColor(ioc.confidence)} size="small" />
                </TableCell>
                <TableCell>{ioc.source.join(', ')}</TableCell>
                <TableCell>{ioc.notes}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default HybridIOCTable;
